<template>
  <div class="go-to-component" v-click-away="validateGoTo">
    <IslandBadge type="goto" :draggable="false"></IslandBadge>
    <div class="go-to-component__go-to">
      <div class="go-to-component__content">
        <TagBox value="go to" />
        <div class="go-to-component__transition-picker">
          <TransitionPicker
            :transition="state.transition"
            @transition-selected="updateTransition"
            :show-none-transition="false"
          />
          <p v-if="isPayloadInvalid" class="help is-danger">Must not contain an invalid transition</p>
          <p v-else-if="isRequiredInvalid" class="help is-danger">Must select a transition</p>
        </div>
      </div>
    </div>
    <div class="invalid-warning" v-if="invalid">
      <ErrorIcon class="icon-1-3x"></ErrorIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import TagBox from '@/shared/components/TagBox.vue';
import { required } from '@vuelidate/validators';
import { StateViewModel } from '@/features/bots/models/designer/stateViewModel';
import IslandBadge from './IslandBadge.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import { computed, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

const state = ref<StateViewModel>({
  id: '',
  component: 'GoTo',
});
const invalid = ref(true);

const validations = {
  state: {
    transition: {
      required,
      type: {
        required,
      },
      payload: {
        payloadValid(payload: string) {
          if (payload) {
            return !isTransitionPayloadInvalid(payload).value;
          }
          return true;
        },
      },
    },
  },
};

const $v = useVuelidate(validations, { state });
const isRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.transition));
const isPayloadInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.transition, 'payloadValid'),
);

const store = useStore();
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

onMounted(() => {
  if (props.value) {
    state.value = props.value;
    invalid.value = state.value.invalid ?? false;
  }
});

function updateTransition(transition: TransitionViewModel) {
  state.value.transition = transition;
  emitUpdatedEvent();
}

function validateGoTo() {
  $v.value.state.$touch();
}

function emitUpdatedEvent() {
  invalid.value = $v.value.state.transition.$invalid;
  state.value.invalid = invalid.value;
  emit('state-updated', state.value);
}
</script>

<style lang="scss" scoped>
.go-to-component {
  display: flex;
  flex: 1;
  &__go-to {
    padding: 1em 1em;
    flex: 1;
  }
  &__container {
    padding: 10px 5px 5px;
  }
  &__content {
    position: relative;
    display: flex;
    flex-direction: row;
    padding: 0.5rem 45px 0.5rem 25px;
    border: 1px solid transparent;
    gap: 30px;
    flex: 1;
  }
  &__transition-picker {
    flex: 1;
  }
}
</style>
